import { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Card, CardContent } from "./ui/card";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { Download, Upload, FileSpreadsheet, Check, AlertCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Supported image types
const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg", 
  "image/png",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/svg+xml",
  "image/tiff"
].join(",");

const BatchImportExport = ({ isOpen, onClose, onImportComplete }) => {
  const { t, isRTL } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const downloadTemplate = () => {
    const template = [
      {
        name: "Product Name",
        name_ar: "اسم المنتج بالعربية",
        brand: "Brand",
        category_id: "category-id-here",
        sale_price: 50.00,
        cost_price: 30.00,
        stock_quantity: 100,
        min_stock_alert: 10,
        country_of_origin: "USA",
        description: "Product description",
        sku: "SKU-001",
        batch_number: "BATCH-001",
        expiry_date: "2025-12-31",
        is_featured: false,
        is_visible: true,
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Products Template");
    
    // Add column widths
    ws['!cols'] = [
      { wch: 25 }, { wch: 30 }, { wch: 15 }, { wch: 25 },
      { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 15 },
      { wch: 15 }, { wch: 40 }, { wch: 15 }, { wch: 15 },
      { wch: 15 }, { wch: 12 }, { wch: 12 }
    ];

    XLSX.writeFile(wb, "products_import_template.xlsx");
    toast.success(t("downloadTemplate"));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResults(null);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        toast.error("No data found in file");
        return;
      }

      // Send to backend
      const response = await axios.post(
        `${API}/products/batch-import`,
        { products: jsonData },
        { headers: getAuthHeaders() }
      );

      setImportResults(response.data);
      
      if (response.data.success_count > 0) {
        toast.success(`${response.data.success_count} ${t("importSuccess")}`);
        onImportComplete?.();
      }
      
      if (response.data.error_count > 0) {
        toast.error(`${response.data.error_count} products failed to import`);
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || t("importFailed"));
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const exportProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/all`, {
        headers: getAuthHeaders()
      });

      const products = response.data.map(p => ({
        name: p.name,
        name_ar: p.name_ar,
        brand: p.brand,
        category_id: p.category_id,
        sale_price: p.sale_price,
        cost_price: p.cost_price,
        stock_quantity: p.stock_quantity,
        min_stock_alert: p.min_stock_alert,
        country_of_origin: p.country_of_origin,
        description: p.description,
        sku: p.sku,
        batch_number: p.batch_number,
        expiry_date: p.expiry_date,
        is_featured: p.is_featured,
        is_visible: p.is_visible,
        barcode: p.barcode,
      }));

      const ws = XLSX.utils.json_to_sheet(products);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Products");
      
      XLSX.writeFile(wb, `products_export_${Date.now()}.xlsx`);
      toast.success(t("reportExported"));
    } catch (error) {
      toast.error("Export failed");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl bg-slate-800 border-slate-700 text-white" dir={isRTL ? "rtl" : "ltr"}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            {t("importProducts")} / {t("exportProducts")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Export Section */}
          <Card className="bg-slate-700 border-slate-600">
            <CardContent className="p-6">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Download className="w-5 h-5 text-green-400" />
                {t("exportProducts")}
              </h3>
              <p className="text-sm text-slate-300 mb-4">
                {isRTL 
                  ? "تصدير جميع المنتجات إلى ملف Excel"
                  : "Export all products to an Excel file"
                }
              </p>
              <Button onClick={exportProducts} className="bg-green-600 hover:bg-green-700">
                <Download className="w-4 h-4 mr-2" />
                {t("exportProducts")}
              </Button>
            </CardContent>
          </Card>

          {/* Import Section */}
          <Card className="bg-slate-700 border-slate-600">
            <CardContent className="p-6">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-400" />
                {t("importProducts")}
              </h3>
              <p className="text-sm text-slate-300 mb-4">
                {isRTL 
                  ? "استيراد المنتجات من ملف Excel. قم بتحميل القالب أولاً."
                  : "Import products from an Excel file. Download the template first."
                }
              </p>
              
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" onClick={downloadTemplate} className="border-slate-500">
                  <Download className="w-4 h-4 mr-2" />
                  {t("downloadTemplate")}
                </Button>
                
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {importing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {t("uploadFile")}
                </Button>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>

              {/* Import Results */}
              {importResults && (
                <div className="mt-4 p-4 bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-4">
                    {importResults.success_count > 0 && (
                      <div className="flex items-center gap-2 text-green-400">
                        <Check className="w-5 h-5" />
                        <span>{importResults.success_count} imported</span>
                      </div>
                    )}
                    {importResults.error_count > 0 && (
                      <div className="flex items-center gap-2 text-red-400">
                        <AlertCircle className="w-5 h-5" />
                        <span>{importResults.error_count} failed</span>
                      </div>
                    )}
                  </div>
                  {importResults.errors?.length > 0 && (
                    <div className="mt-2 text-xs text-red-300">
                      {importResults.errors.slice(0, 5).map((err, i) => (
                        <p key={i}>• {err}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Supported Formats */}
          <p className="text-xs text-slate-400 text-center">
            {isRTL 
              ? "الملفات المدعومة: .xlsx, .xls, .csv"
              : "Supported files: .xlsx, .xls, .csv"
            }
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { BatchImportExport, ACCEPTED_IMAGE_TYPES };
